import React, { useMemo, useState } from 'react';
import moment from 'moment';
import XLSX from 'xlsx';
import { View, Text, Pressable, TextInput } from '../lib/react-native-compat';
import { Chip, OutlineButton } from './procurementFilters';

// Change log for the Billing tab's Rates & Rules view — an append-only record
// of every rate, provision, markup, setting and map edit made on a job, so a
// rate that moved between two invoices is traceable to the person and the day
// that moved it. Forecast_User_Data keeps only DSS_Updated_By/Datetime on the
// live row, which answers "who touched this last" and nothing else.
//
// Storage rides the same per-job Billing rows as the rate schedule itself
// (BU_ID = the job, Version_GUID = BILLING_VERSION):
//   fieldName 'ratelog' rowId 'rl_<ts36><seq><rnd>' — one row per changed
//     field. Forecast_Value = the new value, Notes = { t: 'YYYY-MM-DD HH:mm',
//     u: user, g: kind, s?: section, n: item, f: field, o?: old value,
//     a?: 'add'|'del' (absent = an edit) }.
// Rows are never updated or deleted: a new id every time is what makes the
// log append-only, and nothing in the app writes over one.

export const RATE_LOG_FIELD = 'ratelog';

// Notes is nvarchar(1024) and a truncated JSON blob loses the whole entry, so
// every free-text field is capped well inside it.
const CAP = { name: 140, field: 48, val: 240, user: 30, sec: 60 };

let logSeq = 0;
// Unique without coordination: millisecond + a per-session counter + noise, so
// two people saving in the same millisecond can't land on the same row id.
export const newLogId = () => `rl_${Date.now().toString(36)}${(logSeq += 1).toString(36)}${Math.random().toString(36).slice(2, 5)}`;

// Booleans read as Yes/No and blanks as nothing — the log is read, not parsed,
// and 'false' vs '' in a From column tells nobody anything.
export const fmtLogVal = (v) => {
    if (v === true) return 'Yes';
    if (v === false) return 'No';
    if (v == null) return '';
    return String(v);
};

// Two values are "the same" when they read the same — 1001 and '1001' are not
// a change, and neither is '' vs undefined. Numbers compare numerically so a
// settings blob's first normalizing save doesn't log every field it touched.
export const sameVal = (a, b) => {
    const x = fmtLogVal(a).trim();
    const y = fmtLogVal(b).trim();
    if (x === y) return true;
    if (x === '' || y === '') return false;
    const nx = Number(x);
    const ny = Number(y);
    if (Number.isFinite(nx) && Number.isFinite(ny)) return nx === ny;
    return false;
};

export const LOG_KINDS = [
    ['rate', 'Rates'],
    ['rule', 'Provisions'],
    ['cfg', 'Rate math'],
    ['settings', 'Markup & settings'],
    ['ratemap', 'Labor map'],
    ['eqmap', 'Equipment map'],
];
const KIND_LABEL = Object.fromEntries(LOG_KINDS);

// ── Serialization ──

// entries: [{ kind, sec?, name, field, from, to, act? }] → saveRows rows.
export const logRows = (entries, userName) => {
    const t = moment().format('YYYY-MM-DD HH:mm');
    const u = String(userName || '').slice(0, CAP.user);
    return (entries || []).map((e) => ({
        fieldName: RATE_LOG_FIELD,
        rowId: newLogId(),
        value: fmtLogVal(e.to).slice(0, CAP.val),
        notes: JSON.stringify({
            t,
            u,
            g: e.kind,
            ...(e.sec ? { s: String(e.sec).slice(0, CAP.sec) } : {}),
            n: String(e.name ?? '').slice(0, CAP.name),
            f: String(e.field ?? '').slice(0, CAP.field),
            ...(fmtLogVal(e.from) ? { o: fmtLogVal(e.from).slice(0, CAP.val) } : {}),
            ...(e.act && e.act !== 'edit' ? { a: e.act } : {}),
        }),
    }));
};

export const parseRateLog = (userRows) => {
    const out = [];
    (userRows || []).forEach((x) => {
        if (x?.Forecast_Field_Name !== RATE_LOG_FIELD) return;
        let p = null;
        try { p = x.Notes ? JSON.parse(x.Notes) : null; } catch (e) { /* malformed blob — drop the entry */ }
        if (!p || !p.t) return;
        out.push({
            id: String(x.Forecast_Row_ID || ''),
            ts: String(p.t),
            user: String(p.u || ''),
            kind: String(p.g || ''),
            sec: String(p.s || ''),
            name: String(p.n || ''),
            field: String(p.f || ''),
            from: String(p.o || ''),
            to: String(x.Forecast_Value ?? ''),
            act: p.a === 'add' || p.a === 'del' ? p.a : 'edit',
        });
    });
    // Newest first. The row id carries the millisecond the stamp rounds down
    // to a minute, so it breaks ties in save order, not alphabetically.
    return out.sort((a, b) => String(b.ts).localeCompare(String(a.ts)) || String(b.id).localeCompare(String(a.id)));
};

// ── Diffing a Save into entries ──

// Which fields of a rate row are worth a log line, and what to call them.
const RATE_FIELDS = {
    L: [['name', 'Classification'], ['sec', 'Section'], ['note', 'Note'], ['wage', 'Wage']],
    E: [['name', 'Description'], ['sec', 'Section'], ['notes', 'Note'], ['uom', 'UoM'], ['rate', 'Rate'], ['fu', 'Burns fuel']],
    F: [['name', 'Index'], ['adder', 'Adder %']],
};
const isRuleRow = (r) => !!r && (r.t === 'R' || String(r.id || '').startsWith('ru_'));
// A provision has no name of its own, so it is identified by its section.
const rowName = (r) => (isRuleRow(r) ? `${r.sec || ''} provision` : String(r?.name || ''));
// The value a row is fundamentally about — what an add or a delete logs.
const rowValue = (r) => {
    if (isRuleRow(r)) return r.text;
    if (r?.t === 'L') return r.wage;
    if (r?.t === 'E') return r.rate;
    if (r?.t === 'F') return r.adder;
    return '';
};

// The rate schedule's half of a Save. base = mergeRateSchedule before the
// edits, next = the same rows with the drafts folded in (plus the unsaved
// adds), and the draft maps as the component holds them.
export const rateSaveEntries = ({
    base, next, patch, dels, adds, cfgAfter, mapPatch, laborMap, eqMapPatch, equipMap, equipName,
}) => {
    const out = [];
    const byId = new Map();
    [...(base?.labor || []), ...(base?.equip || []), ...(base?.fuel || []), ...(base?.rules || [])]
        .forEach((r) => byId.set(r.id, r));

    if (cfgAfter) {
        const cfg = base?.cfg || {};
        if (!sameVal(cfg.m, cfgAfter.m)) {
            out.push({ kind: 'cfg', name: 'Labor billing', field: 'Wage multiplier', from: cfg.m, to: cfgAfter.m });
        }
        if (!sameVal(cfg.otf, cfgAfter.otf)) {
            out.push({ kind: 'cfg', name: 'Labor billing', field: 'OT factor', from: cfg.otf, to: cfgAfter.otf });
        }
    }

    Object.keys(patch || {}).forEach((id) => {
        const b = byId.get(id);
        const a = (next || []).find((x) => x.id === id);
        if (!b || !a) return;
        const rule = isRuleRow(b);
        const specs = rule ? [['text', 'Text']] : (RATE_FIELDS[b.t] || []);
        specs.forEach(([f, label]) => {
            if (sameVal(b[f], a[f])) return;
            out.push({
                kind: rule ? 'rule' : 'rate',
                sec: a.sec || b.sec || '',
                name: rowName(b),
                field: label,
                from: b[f],
                to: a[f],
            });
        });
    });

    Object.keys(dels || {}).forEach((id) => {
        const b = byId.get(id);
        if (!b) return;
        out.push({
            kind: isRuleRow(b) ? 'rule' : 'rate',
            sec: b.sec || '',
            name: rowName(b),
            field: 'Row',
            from: rowValue(b),
            to: '',
            act: 'del',
        });
    });

    (adds || []).forEach((a) => {
        out.push({
            kind: isRuleRow(a) ? 'rule' : 'rate',
            sec: a.sec || '',
            name: rowName(a),
            field: 'Row',
            from: '',
            to: rowValue(a),
            act: 'add',
        });
    });

    Object.entries(mapPatch || {}).forEach(([jde, volta]) => {
        const was = (laborMap || {})[jde] || '';
        if (sameVal(was, volta)) return;
        out.push({ kind: 'ratemap', name: jde, field: 'Rate sheet class', from: was, to: volta || '' });
    });

    Object.entries(eqMapPatch || {}).forEach(([code, id]) => {
        const was = (equipMap || {})[code] || '';
        if (sameVal(was, id)) return;
        // Stored as the sheet row's content hash; the log shows the row.
        const nameOf = (rid) => (rid ? (equipName && equipName(rid)) || rid : '');
        out.push({ kind: 'eqmap', name: code, field: 'Rate sheet row', from: nameOf(was), to: nameOf(id) });
    });

    return out;
};

// ── Diffing the settings blob ──

const SETTINGS_LABELS = {
    prefix: 'Summary prefix',
    next: 'Next summary number',
    terms: 'Payment terms',
    signerName: 'Signer name',
    signerEmail: 'Signer email',
    signerCc: 'Signature CC',
    signers: 'Signer map',
    approvers: 'Approvers',
    forceTo: 'Force report to',
    forceCc: 'Force report CC',
    'pd.rate': 'Per diem rate',
    'pd.min': 'Per diem — days that earn the week',
    'pd.full': 'Per diem — days paid at the threshold',
    'pd.ws': 'Per diem week start',
    'fuel.on': 'Fuel surcharge',
    'fuel.exFuel': 'Fuel surcharge — exclude fuel purchases',
    'pt.on': 'Pass-through costs',
    'pt.kw': 'Pass-through keywords',
    'pt.ct': 'Pass-through cost types',
    'ct.on': 'Contract billability rules',
    ru: 'Rentals on HeavyJob hours from',
};
const MK_LABELS = { Equipment: 'Non-rate-sheet equipment' };

export const settingsLabel = (path) => {
    if (SETTINGS_LABELS[path]) return SETTINGS_LABELS[path];
    const mk = /^mk\.(.+)$/.exec(path);
    if (mk) return `${MK_LABELS[mk[1]] || mk[1]} markup %`;
    const q = /^fuel\.q\.(.+)$/.exec(path);
    if (q) return `Diesel price ${q[1]}`;
    return path;
};

const isPlain = (v) => !!v && typeof v === 'object' && !Array.isArray(v);

// Every leaf that differs, as dotted paths. Arrays compare as JSON — the
// signer map is a list of rows and a per-index diff would read worse than one
// "Signer map changed" line.
export const flatDiff = (before, after, prefix = '') => {
    const out = [];
    const keys = [...new Set([...Object.keys(before || {}), ...Object.keys(after || {})])];
    keys.forEach((k) => {
        const path = prefix ? `${prefix}.${k}` : k;
        const a = (before || {})[k];
        const b = (after || {})[k];
        if (isPlain(a) || isPlain(b)) {
            out.push(...flatDiff(isPlain(a) ? a : {}, isPlain(b) ? b : {}, path));
            return;
        }
        if (Array.isArray(a) || Array.isArray(b)) {
            const sa = JSON.stringify(a || []);
            const sb = JSON.stringify(b || []);
            if (sa !== sb) out.push({ path, from: sa, to: sb });
            return;
        }
        if (!sameVal(a, b)) out.push({ path, from: a, to: b });
    });
    return out;
};

export const settingsSaveEntries = (before, after) => flatDiff(before, after).map((d) => ({
    kind: 'settings',
    name: settingsLabel(d.path),
    field: d.path,
    from: d.from,
    to: d.to,
}));

// ── The History tab ──

const th = { fontSize: 10, fontWeight: '700', color: '#64748b', letterSpacing: 0.4, paddingTop: 6, paddingBottom: 6, paddingRight: 8 };
const td = { fontSize: 12, color: '#1e293b', paddingTop: 6, paddingBottom: 6, paddingRight: 8 };
const card = {
    backgroundColor: '#fff', borderRadius: 8, borderWidth: 1, borderColor: '#e2e8f0',
    paddingTop: 12, paddingBottom: 12, paddingLeft: 16, paddingRight: 16, marginBottom: 10,
};
const W = { when: 120, who: 140, what: 130, item: 240, field: 170, from: 150 };
const PAGE = 60;

const ACT_TONE = { add: '#15803d', del: '#b91c1c', edit: '#1e293b' };
const ACT_LABEL = { add: 'Added', del: 'Removed', edit: '' };

const RateChangeLog = ({ userRows, jobKey, jobName }) => {
    const entries = useMemo(() => parseRateLog(userRows), [userRows]);
    const [kind, setKind] = useState('All');
    const [q, setQ] = useState('');
    const [shown, setShown] = useState(PAGE);

    const counts = useMemo(() => {
        const m = {};
        entries.forEach((e) => { m[e.kind] = (m[e.kind] || 0) + 1; });
        return m;
    }, [entries]);

    const filtered = useMemo(() => {
        const needle = q.trim().toLowerCase();
        return entries.filter((e) => {
            if (kind !== 'All' && e.kind !== kind) return false;
            if (!needle) return true;
            return [e.name, e.field, e.from, e.to, e.user, e.sec, KIND_LABEL[e.kind] || e.kind]
                .some((v) => String(v || '').toLowerCase().includes(needle));
        });
    }, [entries, kind, q]);

    const exportXlsx = () => {
        const rows = filtered.map((e) => ({
            When: e.ts,
            Who: e.user,
            What: KIND_LABEL[e.kind] || e.kind,
            Section: e.sec,
            Item: e.name,
            Field: e.field,
            Action: ACT_LABEL[e.act] || 'Changed',
            From: e.from,
            To: e.to,
        }));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), 'Change log');
        XLSX.writeFile(wb, `Rates and Rules history ${jobKey || ''} ${moment().format('YYYY-MM-DD')}.xlsx`);
    };

    return (
        <View style={card}>
            <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 10 }}>
                <View style={{ flexGrow: 1, marginRight: 12 }}>
                    <Text style={{ fontSize: 13, fontWeight: '700', color: '#0f172a' }}>Version history</Text>
                    <Text style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                        {`Every rate, provision, markup and map change saved on job ${jobKey || '—'}${jobName ? ` — ${jobName}` : ''}. Append-only: entries are never edited or removed.`}
                    </Text>
                </View>
                <OutlineButton label="Export to Excel" onPress={exportXlsx} disabled={!filtered.length} />
            </View>

            <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', marginBottom: 6 }}>
                <Chip label={`All (${entries.length})`} active={kind === 'All'} onPress={() => { setKind('All'); setShown(PAGE); }} />
                {LOG_KINDS.filter(([k]) => counts[k]).map(([k, label]) => (
                    <Chip key={k} label={`${label} (${counts[k]})`} active={kind === k} onPress={() => { setKind(k); setShown(PAGE); }} />
                ))}
                <View style={{ flexGrow: 1 }} />
                <TextInput
                    value={q}
                    onChangeText={(v) => { setQ(v); setShown(PAGE); }}
                    placeholder="Search the log"
                    style={{
                        fontSize: 12, borderRadius: 4, borderColor: '#cbd5e1', width: 220,
                        paddingTop: 4, paddingBottom: 4, paddingLeft: 6, paddingRight: 6, marginBottom: 6,
                    }}
                />
            </View>

            {!entries.length ? (
                <Text style={{ fontSize: 12, color: '#64748b', paddingTop: 8, paddingBottom: 6 }}>
                    No changes recorded yet — this job is still billing off the default rate schedule. The next save here is the first entry.
                </Text>
            ) : !filtered.length ? (
                <Text style={{ fontSize: 12, color: '#64748b', paddingTop: 8, paddingBottom: 6 }}>
                    No entries match that filter.
                </Text>
            ) : (
                <>
                    <View style={{ flexDirection: 'row', borderBottomWidth: 1, borderColor: '#e2e8f0' }}>
                        <Text style={{ ...th, width: W.when }}>WHEN</Text>
                        <Text style={{ ...th, width: W.who }}>WHO</Text>
                        <Text style={{ ...th, width: W.what }}>WHAT</Text>
                        <Text style={{ ...th, width: W.item }}>ITEM</Text>
                        <Text style={{ ...th, width: W.field }}>FIELD</Text>
                        <Text style={{ ...th, width: W.from }}>FROM</Text>
                        <Text style={{ ...th, flexGrow: 1 }}>TO</Text>
                    </View>
                    {filtered.slice(0, shown).map((e) => (
                        <View key={e.id} style={{ flexDirection: 'row', borderBottomWidth: 1, borderColor: '#f1f5f9' }}>
                            <Text style={{ ...td, width: W.when, color: '#475569' }}>{e.ts}</Text>
                            <Text style={{ ...td, width: W.who, color: '#475569' }}>{e.user || '—'}</Text>
                            <Text style={{ ...td, width: W.what, color: '#475569' }}>{KIND_LABEL[e.kind] || e.kind}</Text>
                            <View style={{ width: W.item, paddingRight: 8 }}>
                                <Text style={{ ...td, paddingRight: 0, fontWeight: '600' }}>{e.name || '—'}</Text>
                                {e.sec ? <Text style={{ fontSize: 10, color: '#94a3b8', paddingBottom: 4 }}>{e.sec}</Text> : null}
                            </View>
                            <View style={{ width: W.field, paddingRight: 8 }}>
                                <Text style={{ ...td, paddingRight: 0 }}>{e.field || '—'}</Text>
                                {e.act !== 'edit' ? (
                                    <Text style={{ fontSize: 10, fontWeight: '700', color: ACT_TONE[e.act], paddingBottom: 4 }}>
                                        {ACT_LABEL[e.act]}
                                    </Text>
                                ) : null}
                            </View>
                            <Text style={{ ...td, width: W.from, color: '#94a3b8' }}>{e.from || '—'}</Text>
                            <Text style={{ ...td, flexGrow: 1, fontWeight: '600', color: ACT_TONE[e.act] }}>{e.to || '—'}</Text>
                        </View>
                    ))}
                    {filtered.length > shown ? (
                        <Pressable onPress={() => setShown((n) => n + PAGE)} style={{ paddingTop: 8 }}>
                            <Text style={{ fontSize: 11, color: '#1e40af', fontWeight: '600' }}>
                                {`Show ${Math.min(PAGE, filtered.length - shown)} more (${filtered.length - shown} left)`}
                            </Text>
                        </Pressable>
                    ) : null}
                </>
            )}
        </View>
    );
};

export default RateChangeLog;
